import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSelection } from '../context/SelectionContext';
import { useTranslation } from 'react-i18next';
import { formatPrice, formatKwota } from '../utils';

const UNIT = { panel: 'W', inwerter: 'kW', magazyn: 'kWh' }

const shortName = (name, n = 18) => {
  if (!name) return ''
  const s = String(name).trim()
  return s.length > n ? `${s.slice(0, n).trim()}…` : s
}

const powerLabel = (it) => {
  const p = it.category === 'magazyn' ? it.capacity : it.power
  if (p === null || p === undefined || String(p).trim() === '') return null
  return `${p} ${UNIT[it.category] || ''}`.trim()
}

const CompareWidget = () => {
  const { t } = useTranslation('lista')
  const { comparisonItems, comparisonCount, removeComparison } = useSelection()
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)

  if (comparisonCount === 0) return null
  // Na stronie /porownanie zarządzanie odbywa się w tabeli — nie dublujemy widgetu.
  if (location.pathname === '/porownanie') return null

  const canCompare = comparisonCount >= 2

  return (
    <>
      {/* ===== Desktop: pływająca karta prawy dolny róg ===== */}
      <div className="hidden md:block fixed right-6 bottom-6 z-40 w-[340px]">
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {/* Cały nagłówek zwija/rozwija */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-semibold text-gray-800">
              {t('porownanie.naglowek')} <span className="text-blue-600">{comparisonCount}/4</span>
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
          </button>

          {!collapsed && (
            <>
              <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-50">
                {comparisonItems.map((it) => {
                  const pw = powerLabel(it)
                  const price = formatPrice(it.price, 0)
                  return (
                    <div key={`${it.category}:${it.id}`} className="flex items-center gap-3 px-4 py-2.5">
                      <img src={it.img_url} alt="" className="w-10 h-10 object-contain rounded border border-gray-100 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{shortName(it.name)}</p>
                        <p className="text-xs text-gray-500">
                          {pw ? `${pw}` : ''}{pw && price !== null ? ' · ' : ''}{price !== null ? formatKwota(it.price, 0) : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => removeComparison(it.id, it.category)}
                        aria-label={t('porownanie.usun')}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )
                })}
              </div>
              <div className="px-4 py-3 border-t border-gray-100">
                {!canCompare && (
                  <p className="text-xs text-gray-500 mb-2">{t('porownanie.dodaj_jeszcze')}</p>
                )}
                <button
                  disabled={!canCompare}
                  onClick={() => navigate('/porownanie')}
                  className={`w-full rounded-lg py-2 text-sm font-semibold transition-colors ${
                    canCompare ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t('porownanie.porownaj_produkty')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== Mobile: niski sticky bar ===== */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-gray-200 px-4 py-2.5 flex items-center justify-between shadow-[0_-1px_4px_rgba(0,0,0,0.05)]">
        <span className="text-sm font-semibold text-gray-800">
          {t('porownanie.naglowek')} <span className="text-blue-600">{comparisonCount}/4</span>
        </span>
        <button
          disabled={!canCompare}
          onClick={() => navigate('/porownanie')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
            canCompare ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
          }`}
        >
          {t('porownanie.porownaj')}
        </button>
      </div>
    </>
  )
}

export default CompareWidget
